

















































































































































































































































































































































































































































































import {Component} from 'vue-property-decorator'
import ECharts from 'vue-echarts/components/ECharts.vue'
import 'echarts/lib/chart/pie'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/title'
import {TokensTableItemsCollection} from '@/model/collection/TokensTableItemsCollection'
import {MixinScreenSize} from '@/components/mixins/MixinScreenSize'
import {GasbotHelper} from '@/helpers/GasbotHelper'

type TokenUI = {
  name: string
  scriptHash: string
  imageUrl: string | null
  amountStr: string
  amount: number
  wallets: number
  color?: string
}

@Component({
  components: {'e-charts': ECharts},
})
export default class GasbotStatisticsView extends MixinScreenSize {
  statisticsData: {
    totalServers: number
    holdingWallets: number
    totalWallets: number
    totalTokens: number
    totalTokensUSD: number
    tokens: TokenUI[]
  } | null = null

  tokensTableItemsCollection = new TokensTableItemsCollection()

  private BASE_DISTINCT_HEX: string[] = [
    '#1f77b4',
    '#ff7f0e',
    '#2ca02c',
    '#d62728',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#7f7f7f',
    '#bcbd22',
    '#17becf',
    '#393b79',
    '#637939',
    '#8c6d31',
    '#843c39',
    '#7b4173',
    '#3182bd',
    '#e6550d',
    '#31a354',
    '#756bb1',
    '#636363',
    '#9c9ede',
    '#cedb9c',
    '#e7ba52',
    '#ad494a',
    '#a55194',
    '#6baed6',
    '#fd8d3c',
    '#74c476',
    '#bcbddc',
    '#969696',
  ]

  get orderedTokens(): TokenUI[] {
    if (!this.statisticsData) return []
    const priority = (t: TokenUI) =>
      t.name.toUpperCase() === 'GAS'
        ? 0
        : t.name.toUpperCase() === 'NEO'
        ? 1
        : 2
    return [...this.statisticsData.tokens].sort(
      (a, b) => priority(a) - priority(b)
    )
  }

  get legendRows() {
    if (!this.statisticsData) return []
    const palette = this.buildPalette()
    return this.orderedTokens.map(t => ({
      name: t.name,
      scriptHash: t.scriptHash,
      amountStr: t.amountStr,
      wallets: t.wallets,
      color: palette.get(t.scriptHash) || '#9CA3AF',
    }))
  }

  get distributionOptions() {
    if (!this.statisticsData) return {}
    const {data, colorByKey} = this.getChartDataWithOthers()
    return {
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: '#111827',
        textStyle: {color: '#fff'},
        extraCssText: 'border-radius:10px;padding:10px;',
        formatter: (params: any) => {
          const d = params.data || {}
          const amt = d.amountStr ?? String(d.value)
          const usd = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
          }).format(Number(d.usd ?? 0))
          const hashLine = d.scriptHash
            ? `
            <div style="opacity:.85">Hash:</div>
            <div style="font-family:monospace">${d.scriptHash}</div>`
            : ''
          return `
            <div style="min-width:220px">
              <div style="font-weight:700;margin-bottom:4px;">${params.marker} ${params.name}</div>
              ${hashLine}
              <div style="margin-top:6px;display:flex;justify-content:space-between;">
                <span>Amount:</span><span><b>${amt}</b></span>
              </div>
              <div style="display:flex;justify-content:space-between;">
                <span>Value (USD):</span><span><b>${usd}</b></span>
              </div>
            </div>
          `
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['58%', '82%'],
          center: ['50%', '50%'],
          hoverOffset: 6,
          label: {show: false},
          labelLine: {show: false},
          minAngle: 2,
          data: data.map(d => ({
            ...d,
            itemStyle: {color: colorByKey.get(d.key) || '#9CA3AF'},
          })),
        },
      ],
    }
  }

  formatUSD(v: number | string, digits = 2): string {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n || 0)
  }

  mounted() {
    this.fetchStatistics()
  }

  buildPalette(): Map<string, string> {
    const m = new Map<string, string>()
    if (!this.statisticsData) return m
    const keys = this.orderedTokens.map(t => t.scriptHash)
    const palette = this.distinctColors(keys.length)
    keys.forEach((key, idx) => m.set(key, palette[idx]))
    return m
  }

  formatInt(v: number | string): string {
    const n = typeof v === 'string' ? parseFloat(v) : v
    return new Intl.NumberFormat('en-US', {maximumFractionDigits: 0}).format(
      n || 0
    )
  }

  truncateHash(h: string, left = 10, right = 8): string {
    if (!h) return ''
    if (h.length <= left + right + 3) return h
    return `${h.slice(0, left)}...${h.slice(-right)}`
  }

  async fetchStatistics() {
    await this.$await.init('fetch-statistics')
    await this.tokensTableItemsCollection.queryAsPage()

    const {
      totalTokens: statisticsTotalTokens,
      totalWallets: statisticsTotalWallets,
      totalServers: statisticsTotalServers,
      holdingWallets: statisticsHoldingWallets,
      tokenWallets: statisticsTokenWallets,
    } = await GasbotHelper.fetchStatistics()

    const statisticsData = {
      totalServers: statisticsTotalServers,
      holdingWallets: statisticsHoldingWallets,
      totalWallets: statisticsTotalWallets,
      tokens: statisticsTotalTokens,
      tokenWallets: statisticsTokenWallets,
    }

    const tokens: TokenUI[] = Object.entries(statisticsData.tokens).map(
      ([hash, amountStr]) => {
        const tokenItem = this.tokensTableItemsCollection.items.find(
          i => i.marketInformation?.hash?.toLowerCase?.() === hash.toLowerCase()
        )
        const symbol: string = tokenItem?.symbol ?? 'Unknown Token'
        const imageUrl: string | null = tokenItem?.imageUrl ?? null
        return {
          name: symbol,
          scriptHash: hash,
          imageUrl,
          amountStr: String(amountStr),
          amount: Number(amountStr),
          wallets: statisticsData.tokenWallets?.[hash] ?? 0,
        }
      }
    )

    const totalTokens = tokens.reduce((acc, t) => acc + (t.amount || 0), 0)
    const totalTokensUSD = tokens.reduce((acc, t) => {
      const price = this.getPriceUSDBySymbol(t.name)
      return acc + (t.amount || 0) * price
    }, 0)

    this.statisticsData = {
      totalServers: statisticsData.totalServers,
      holdingWallets: statisticsData.holdingWallets,
      totalWallets: statisticsData.totalWallets,
      totalTokens,
      tokens,
      totalTokensUSD,
    }
  }

  private distinctColors(count: number): string[] {
    if (count <= this.BASE_DISTINCT_HEX.length) {
      return this.BASE_DISTINCT_HEX.slice(0, count)
    }
    const out = [...this.BASE_DISTINCT_HEX]
    const golden = 137.508
    let hue = 0
    const S = 0.68
    const L = 0.52
    for (let i = this.BASE_DISTINCT_HEX.length; i < count; i++) {
      const h = ((hue % 360) + 360) % 360
      out.push(this.hslToHex(h, S, L))
      hue += golden
    }
    return this.interleaveForContrast(out)
  }

  private hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const hp = h / 60
    const x = c * (1 - Math.abs((hp % 2) - 1))
    let r = 0,
      g = 0,
      b = 0
    if (0 <= hp && hp < 1) [r, g, b] = [c, x, 0]
    else if (1 <= hp && hp < 2) [r, g, b] = [x, c, 0]
    else if (2 <= hp && hp < 3) [r, g, b] = [0, c, x]
    else if (3 <= hp && hp < 4) [r, g, b] = [0, x, c]
    else if (4 <= hp && hp < 5) [r, g, b] = [x, 0, c]
    else if (5 <= hp && hp < 6) [r, g, b] = [c, 0, x]
    const m = l - c / 2
    const R = Math.round((r + m) * 255)
    const G = Math.round((g + m) * 255)
    const B = Math.round((b + m) * 255)
    const toHex = (n: number) => n.toString(16).padStart(2, '0')
    return `#${toHex(R)}${toHex(G)}${toHex(B)}`
  }

  private interleaveForContrast(colors: string[]): string[] {
    const mid = Math.ceil(colors.length / 2)
    const a = colors.slice(0, mid)
    const b = colors.slice(mid)
    const out: string[] = []
    const n = Math.max(a.length, b.length)
    for (let i = 0; i < n; i++) {
      if (i < a.length) out.push(a[i])
      if (i < b.length) out.push(b[i])
    }
    return out
  }

  private getChartDataWithOthers() {
    const palette = this.buildPalette()
    const sorted = this.orderedTokens
      .map(t => {
        const price = this.getPriceUSDBySymbol(t.name)
        const usd = (t.amount || 0) * price
        return {
          key: t.scriptHash,
          name: t.name,
          scriptHash: t.scriptHash,
          value: t.amount,
          amountStr: t.amountStr,
          usd,
        }
      })
      .sort((a, b) => (b.value || 0) - (a.value || 0))

    const total = sorted.reduce((acc, d) => acc + (d.value || 0), 0)
    const biggest = sorted[0]?.value || 0
    const share = total > 0 ? biggest / total : 0
    const DOMINANCE_THRESHOLD = 0.85
    const TOP_N = 5

    if (share > DOMINANCE_THRESHOLD && sorted.length > 2) {
      const top = sorted.slice(0, TOP_N)
      const rest = sorted.slice(TOP_N)
      const others = rest.reduce(
        (acc, d) => {
          acc.value += d.value || 0
          acc.usd += d.usd || 0
          return acc
        },
        {
          key: '__OTHERS__',
          name: 'Others',
          scriptHash: '',
          value: 0,
          amountStr: '',
          usd: 0,
        }
      )
      if (others.value > 0) {
        others.amountStr = String(others.value)
      }
      const data = others.value > 0 ? [...top, others] : top

      const colorByKey = new Map<string, string>([
        ...top.map(d => [d.key, palette.get(d.key) ?? '#9CA3AF'] as const),
        ['__OTHERS__', '#E5E7EB'] as const,
      ])
      return {data, colorByKey}
    }

    const colorByKey = new Map<string, string>()
    sorted.forEach(d => colorByKey.set(d.key, palette.get(d.key) || '#9CA3AF'))
    return {data: sorted, colorByKey}
  }

  private getPriceUSDBySymbol(symbol: string): number {
    const item = this.tokensTableItemsCollection.items.find(
      i => i.symbol === symbol
    )
    return Number(item?.marketInformation?.currentPrice ?? 0)
  }
}
